//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { OverlayIconButton } from '@components/atoms/button/Button';
import { PredictiveSchedulingTooltip } from '@components/harmonyai/HarmonyAITooltip';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { isVideo, isVideoProcessing } from '@content-drawer/media/mediaUtils';
import { getIsContentDrawerMediaRecommendationsEnabled } from '@content-drawer/redux/getContentDrawer';
import { getContentDrawerSettings } from '@content-drawer/selectors/getContentDrawerSettings';
import { getRgbaForThemeHex } from '@utils/getRgbaForThemeHex';
import { withTracking } from '@utils/withTracking';

export interface MediaItemProps {
  isSelected: boolean;
  item: IContentLibraryItem;
  onPreview: (item: IContentLibraryItem) => void;
  onSelect: (item: IContentLibraryItem) => void;
}

export const MediaItem = ({
  isSelected,
  item,
  onPreview,
  onSelect,
}: MediaItemProps) => {
  const drawerSettings = useSelector(getContentDrawerSettings);
  const isContentDrawerMediaRecommendationsEnabled = useSelector(
    getIsContentDrawerMediaRecommendationsEnabled,
  );
  const isVideoType = isVideo(item);
  const isPreviewDisabled = isVideoProcessing(item);

  const isPredictiveSchedulingEnabled =
    isContentDrawerMediaRecommendationsEnabled &&
    item.forecastedPerformance?.expectedHighPerformance &&
    drawerSettings.shouldShowSchedulerForecasting;

  const getPreviewIcon = () => {
    if (isPreviewDisabled) {
      return 'spinner';
    } else {
      return isVideoType ? 'play' : 'zoom';
    }
  };

  const selectMediaItem = (item: IContentLibraryItem) => {
    if (!isPreviewDisabled) {
      onSelect(item);
    }
  };

  return (
    <Wrapper isSelected={isSelected}>
      <ThumbnailButton
        onClick={() => selectMediaItem(item)}
        trackingId={ContentDrawerTrackingIds.SELECT_MEDIA_ITEM}
      >
        <Thumbnail src={item.thumbnailUrl} alt={item.description} />
      </ThumbnailButton>
      {isSelected && (
        <>
          <ShadowBox onClick={() => selectMediaItem(item)} />
          <SelectIconButton
            iconName='checkmark'
            onClick={() => onSelect(item)}
            trackingId={ContentDrawerTrackingIds.SELECT_MEDIA_ITEM}
          />
        </>
      )}
      {isPredictiveSchedulingEnabled && (
        <PredictionIconWrapper>
          <PredictiveSchedulingTooltip />
        </PredictionIconWrapper>
      )}
      <PreviewIconButton
        isDisabled={isPreviewDisabled}
        iconName={getPreviewIcon()}
        onClick={() => onPreview(item)}
        trackingId={ContentDrawerTrackingIds.PREVIEW_MEDIA_ITEM}
      />
    </Wrapper>
  );
};

const Wrapper = styled.li<{ isSelected: boolean }>(
  ({ theme, isSelected }) => css`
    position: relative;
    border-radius: ${theme.borderWidths[3]};
    aspect-ratio: 1;
    overflow: hidden;
    ${isSelected &&
    `border: ${theme.borderWidths[3]} solid ${theme.colors.aliases.primary};`}
  `,
);

const ThumbnailButton = withTracking(styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  border: unset;
  padding: unset;
  box-shadow: unset;
  overflow: hidden;

  :hover {
    box-shadow: unset;
  }
`);

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ShadowBox = styled.div(
  ({ theme }) => `
    position: absolute;
    top: ${theme.spacings[0]};
    height: 60%;
    width: 100%;
    background: linear-gradient(
      to bottom,
      ${getRgbaForThemeHex(theme.colors.black, 0.6)},
      ${getRgbaForThemeHex(theme.colors.black, 0)}
    );
  `,
);

const SelectIconButton = styled(OverlayIconButton)(
  ({ theme }) => `
    position: absolute;
    top: ${theme.spacings[2]};
    left: ${theme.spacings[2]};
    background-color: ${theme.colors.aliases.primary};
  `,
);

const PreviewIconButton = styled(OverlayIconButton)(
  ({ theme }) => `
    position: absolute;
    right: ${theme.spacings[2]};
    bottom: ${theme.spacings[2]};
  `,
);

const PredictionIconWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    top: ${theme.spacings[2]};
    right: ${theme.spacings[2]};
    height: ${theme.spacings[5]};
    width: ${theme.spacings[5]};

    border-radius: ${theme.borderRadii.pill};
    background-color: ${theme.colors.white};
    box-shadow: ${theme.boxShadows[3]};

    && div {
      height: auto;
    }
  `,
);
